import {
  UserType,
  GetUsersResponse,
  OrganizationUnitNameAndId,
  OrganizationUnit,
  OrganizationUnitType,
} from '@inplay/models';
import {
  UserPresentationStrategy,
  FieldConfig,
} from '../userPresentationStrategy.interface';
import i18n from '../../../i18n';
import { userTypeOptions } from '../../../component/userModal/helper';
import { isUndefined } from 'lodash';
import { Option } from '../../../component/dropDownInput/dropDownInput.type';
import {
  filterAndConvertOrgUnitsToOption,
  getChildOrganizationUnitOptions,
} from '../../../utils/helper';
import moment from 'moment';

export class OrganizationAdminPresentationStrategy
  implements UserPresentationStrategy
{
  private translate: (key: string) => string;
  private userDetail: GetUsersResponse;

  constructor(userDetail: GetUsersResponse) {
    /**
     * i18next's type definitions don't properly handle the return type of i18n.t()
     * function can return string | object | array, but we know in our usage it will always return string
     * This is a known TypeScript limitation with i18next - see: https://github.com/i18next/i18next/issues/1849
     */
    //@ts-ignore
    this.translate = (key: string) => i18n.t(key) || key;
    this.userDetail = userDetail;
  }

  public getViewableFieldsOfUser(
    subject: GetUsersResponse,
  ): Record<string, any> {
    console.log(`[subject]`, {
      subject,
    });
    switch (subject.type) {
      case UserType.ORGANIZATION_ADMIN:
        return {
          [this.translate('userManagement.userType')]: subject.type,
          [this.translate('userManagement.name')]:
            subject.firstName + ' ' + subject.lastName,
          [this.translate('userManagement.photo')]: subject?.profilePhoto,
          [this.translate('userManagement.organization')]:
            subject.organization.name,
          [this.translate('userManagement.subOrganizations')]:
            this.translate('userManagement.all'),
          [this.translate('userManagement.venue')]:
            this.translate('userManagement.all'),
          [this.translate('userManagement.email')]: subject.email,
          [this.translate('userManagement.mobile')]: subject.mobileNumber,
          [this.translate('userManagement.addedBy')]:
            subject.addedBy?.firstName +
            ' ' +
            subject.addedBy?.lastName +
            ', ' +
            moment(subject.addedBy?.date).format('DD/MM/YYYY'),
        };
      case UserType.SUB_ORGANIZATION_ADMIN:
        return {
          [this.translate('userManagement.userType')]: subject.type,
          [this.translate('userManagement.name')]:
            subject.firstName + ' ' + subject.lastName,
          [this.translate('userManagement.photo')]: subject?.profilePhoto,
          [this.translate('userManagement.organization')]:
            subject.organization.name,
          [this.translate('userManagement.subOrganizations')]:
            subject.subOrganizations && subject.subOrganizations.length > 0
              ? subject.subOrganizations
                  .map(
                    (subOrganization: OrganizationUnitNameAndId) =>
                      subOrganization.name,
                  )
                  .join(', ')
              : '',
          [this.translate('userManagement.venue')]: [
            this.translate('userManagement.all'),
          ],
          [this.translate('userManagement.email')]: subject.email,
          [this.translate('userManagement.mobile')]: subject.mobileNumber,
          [this.translate('userManagement.addedBy')]:
            subject.addedBy?.firstName +
            ' ' +
            subject.addedBy?.lastName +
            ', ' +
            moment(subject.addedBy?.date).format('DD/MM/YYYY'),
        };
      case UserType.VENUE_ADMIN:
        return {
          [this.translate('userManagement.userType')]: subject.type,
          [this.translate('userManagement.name')]:
            subject.firstName + ' ' + subject.lastName,
          [this.translate('userManagement.photo')]: subject?.profilePhoto,
          [this.translate('userManagement.subOrganizations')]:
            subject.subOrganizations && subject.subOrganizations.length > 0
              ? subject.subOrganizations
                  .map(
                    (subOrganization: OrganizationUnitNameAndId) =>
                      subOrganization.name,
                  )
                  .join(', ')
              : '',
          [this.translate('userManagement.venue')]: subject.venue?.name,
          [this.translate('userManagement.email')]: subject.email,
          [this.translate('userManagement.mobile')]: subject.mobileNumber,
          [this.translate('userManagement.addedBy')]:
            subject.addedBy?.firstName +
            ' ' +
            subject.addedBy?.lastName +
            ', ' +
            moment(subject.addedBy?.date).format('DD/MM/YYYY'),
        };
      case UserType.VENUE_MANAGER:
        return {
          [this.translate('userManagement.userType')]: subject.type,
          [this.translate('userManagement.name')]:
            subject.firstName + ' ' + subject.lastName,
          [this.translate('userManagement.photo')]: subject?.profilePhoto,
          [this.translate('userManagement.subOrganizations')]:
            subject.subOrganizations && subject.subOrganizations.length > 0
              ? subject.subOrganizations.map((org) => org.name).join(', ')
              : '',
          [this.translate('userManagement.venue')]: subject.venue?.name,
          [this.translate('userManagement.venueBranches')]:
            subject.venueBranchs && subject.venueBranchs.length > 0
              ? subject.venueBranchs
                  .map(
                    (venueBranch: OrganizationUnitNameAndId) =>
                      venueBranch.name,
                  )
                  .join(', ')
              : '',
          [this.translate('userManagement.email')]: subject.email,
          [this.translate('userManagement.mobile')]: subject.mobileNumber,
          [this.translate('userManagement.addedBy')]:
            subject.addedBy?.firstName +
            ' ' +
            subject.addedBy?.lastName +
            ', ' +
            moment(subject.addedBy?.date).format('DD/MM/YYYY'),
        };
      case UserType.VENUE_STAFF:
        return {
          [this.translate('userManagement.userType')]: subject.type,
          [this.translate('userManagement.name')]:
            subject.firstName + ' ' + subject.lastName,
          [this.translate('userManagement.photo')]: subject?.profilePhoto,
          [this.translate('userManagement.subOrganizations')]:
            subject.subOrganizations && subject.subOrganizations.length > 0
              ? subject.subOrganizations
                  .map(
                    (subOrganization: OrganizationUnitNameAndId) =>
                      subOrganization.name,
                  )
                  .join(', ')
              : '',
          [this.translate('userManagement.venue')]: subject.venue?.name,
          [this.translate('userManagement.venueBranches')]:
            subject?.venueBranchs && subject?.venueBranchs?.length > 0
              ? subject?.venueBranchs
                  .map(
                    (venueBranch: OrganizationUnitNameAndId) =>
                      venueBranch.name,
                  )
                  .join(', ')
              : '',
          [this.translate('userManagement.addedBy')]:
            subject.addedBy?.firstName +
            ' ' +
            subject.addedBy?.lastName +
            ', ' +
            moment(subject.addedBy?.date).format('DD/MM/YYYY'),
        };
      default:
        const error: string = `Incorrect user type or user not allowed to access user type`;
        throw error;
    }
  }

  public getEditableFieldsOfUser(
    user: GetUsersResponse,
    organizationUnits: (OrganizationUnit & { _id: string })[],
  ): FieldConfig[] {
    const commonFields: FieldConfig[] = [
      {
        id: 'userType',
        type: 'readonly',
        label: this.translate('userManagement.userTypeTitle'),
        required: true,
        defaultValue: user.type,
      },
      {
        id: 'firstName',
        type: 'text',
        label: this.translate('userManagement.firstName'),
        required: true,
        placeholder: this.translate('userManagement.firstName'),
        defaultValue: user.firstName,
      },
      {
        id: 'lastName',
        type: 'text',
        label: this.translate('userManagement.lastName'),
        required: true,
        placeholder: this.translate('userManagement.lastName'),
        defaultValue: user.lastName,
      },
      {
        id: 'photo',
        type: 'photo',
        label: this.translate('userManagement.photo'),
        required: false,
        defaultValue: user.profilePhoto,
      },
    ];
    // Fields specific to each user type
    switch (user.type) {
      case UserType.ORGANIZATION_ADMIN:
        return [
          ...commonFields,
          {
            id: 'organization',
            type: 'readonly',
            label: this.translate('userManagement.organization'),
            required: false,
            defaultValue: user.organization.id,
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.ORGANIZATION,
            ),
          },
          {
            id: 'subOrganizations',
            type: 'readonly',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            defaultValue: this.translate('userManagement.all'),
          },
          {
            id: 'email',
            type: 'readonly',
            label: this.translate('userManagement.email'),
            required: true,
            placeholder: this.translate('userManagement.email'),
            defaultValue: user.email || '',
          },
          {
            id: 'mobile',
            type: 'phone',
            label: this.translate('userManagement.mobile'),
            required: false,
            placeholder: this.translate('userManagement.mobile'),
            defaultValue: user.mobileNumber || '',
          },
        ];

      case UserType.SUB_ORGANIZATION_ADMIN:
        return [
          ...commonFields,
          {
            id: 'organization',
            type: 'readonly',
            label: this.translate('userManagement.organization'),
            required: false,
            defaultValue: user.organization.name,
          },
          {
            id: 'subOrganizations',
            type: 'dropdown',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.SUB_ORGANIZATION,
            ),
            placeholder: 'Select',
            defaultValue: user.subOrganizations?.[0]?.id || '',
          },
          {
            id: 'email',
            type: 'readonly',
            label: this.translate('userManagement.email'),
            required: true,
            placeholder: this.translate('userManagement.email'),
            defaultValue: user.email || '',
          },
          {
            id: 'mobile',
            type: 'phone',
            label: this.translate('userManagement.mobile'),
            required: false,
            placeholder: this.translate('userManagement.mobile'),
            defaultValue: user.mobileNumber || '',
          },
        ];

      case UserType.VENUE_ADMIN:
        return [
          ...commonFields,
          {
            id: 'subOrganizations',
            type: 'readonly',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            placeholder: 'Select',
            defaultValue: user.subOrganizations
              ? user.subOrganizations.map(
                  (subOrg: OrganizationUnitNameAndId) => subOrg?.id,
                )
              : '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.SUB_ORGANIZATION,
            ),
          },
          {
            id: 'venue',
            type: 'readonly',
            label: this.translate('userManagement.venue'),
            required: true,
            placeholder: 'Select',
            defaultValue: user.venue?.id || '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.VENUE,
            ),
          },
          {
            id: 'email',
            type: 'readonly',
            label: this.translate('userManagement.email'),
            required: true,
            placeholder: this.translate('userManagement.email'),
            defaultValue: user.email || '',
          },
          {
            id: 'mobile',
            type: 'phone',
            label: this.translate('userManagement.mobile'),
            required: false,
            placeholder: this.translate('userManagement.mobile'),
            defaultValue: user.mobileNumber || '',
          },
        ];

      case UserType.VENUE_MANAGER:
        return [
          ...commonFields,
          {
            id: 'subOrganizations',
            type: 'readonly',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            placeholder: 'Select',
            defaultValue: user.subOrganizations
              ? user.subOrganizations.map(
                  (subOrg: OrganizationUnitNameAndId) => subOrg?.id,
                )
              : '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.SUB_ORGANIZATION,
            ),
          },
          {
            id: 'venue',
            type: 'readonly',
            label: this.translate('userManagement.venue'),
            required: true,
            placeholder: 'Select',
            defaultValue: user.venue?.id || '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.VENUE,
            ),
          },
          {
            id: 'venueBranch',
            type: 'multiselect',
            label: this.translate('userManagement.venueBranches'),
            required: true,
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.VENUE_BRANCH,
              user.venue?.id,
            ),
            placeholder: 'Select',
            defaultValue: user.venueBranchs
              ? user.venueBranchs.map(
                  (branch: OrganizationUnitNameAndId) => branch?.id,
                )
              : '',
          },
          {
            id: 'email',
            type: 'readonly',
            label: this.translate('userManagement.email'),
            required: true,
            placeholder: this.translate('userManagement.email'),
            defaultValue: user.email || '',
          },
          {
            id: 'mobile',
            type: 'phone',
            label: this.translate('userManagement.mobile'),
            required: false,
            placeholder: this.translate('userManagement.mobile'),
            defaultValue: user.mobileNumber || '',
          },
        ];

      case UserType.VENUE_STAFF:
        return [
          ...commonFields,
          {
            id: 'subOrganizations',
            type: 'readonly',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            placeholder: 'Select',
            defaultValue: user.subOrganizations
              ? user.subOrganizations.map(
                  (subOrg: OrganizationUnitNameAndId) => subOrg?.id,
                )
              : '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.SUB_ORGANIZATION,
            ),
          },
          {
            id: 'venue',
            type: 'readonly',
            label: this.translate('userManagement.venue'),
            required: true,
            placeholder: 'Select',
            defaultValue: user.venue?.id || '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.VENUE,
            ),
          },
          {
            id: 'venueBranch',
            type: 'multiselect',
            label: this.translate('userManagement.venueBranches'),
            required: true,
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.VENUE_BRANCH,
              user.venue?.id,
            ),
            placeholder: 'Select',
            defaultValue: user.venueBranchs
              ? user.venueBranchs.map(
                  (branch: OrganizationUnitNameAndId) => branch?.id,
                )
              : '',
          },
          {
            id: 'pin',
            type: 'pin',
            label: this.translate('userManagement.pin'),
            required: false,
            options: [
              {
                label: this.translate('userManagement.custom'),
                value: 'custom',
              },
              {
                label: this.translate('userManagement.autoGenerate'),
                value: 'default',
              },
            ],
          },
        ];
      default:
        const error: string = `Incorrect user type or user not allowed to access user type`;
        throw error;
    }
  }

  public getCreationFieldsConfig(
    newUserType: UserType,
    organizationUnits: (OrganizationUnit & { _id: string })[],
  ): FieldConfig[] {
    if (isUndefined(newUserType)) {
      newUserType = UserType.VENUE_STAFF;
    }
    const allowedUserTypes: UserType[] = [
      UserType.ORGANIZATION_ADMIN,
      UserType.SUB_ORGANIZATION_ADMIN,
      UserType.VENUE_ADMIN,
      UserType.VENUE_MANAGER,
      UserType.VENUE_STAFF,
    ];
    const allowedUserTypeOptions: Option[] = userTypeOptions.filter(
      (userTypeOption: Option) => {
        return allowedUserTypes.includes(userTypeOption.value as UserType);
      },
    );
    const commonFields: FieldConfig[] = [
      {
        id: 'userType',
        type: 'dropdown',
        label: this.translate('userManagement.userTypeTitle'),
        required: true,
        options: allowedUserTypeOptions,
        defaultValue: UserType.VENUE_STAFF,
      },
      {
        id: 'firstName',
        type: 'text',
        label: this.translate('userManagement.firstName'),
        required: true,
        placeholder: this.translate('userManagement.firstName'),
      },
      {
        id: 'lastName',
        type: 'text',
        label: this.translate('userManagement.lastName'),
        required: true,
        placeholder: this.translate('userManagement.lastName'),
      },
      {
        id: 'photo',
        type: 'photo',
        label: this.translate('userManagement.photo'),
        required: true,
        defaultValue: '',
      },
    ];
    // Fields specific to each user type
    switch (newUserType) {
      case UserType.ORGANIZATION_ADMIN:
        return [
          ...commonFields,
          {
            id: 'organization',
            type: 'readonly',
            label: this.translate('userManagement.organization'),
            required: false,
            defaultValue: this.userDetail?.organization?.id || '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.ORGANIZATION,
            ),
          },
          {
            id: 'subOrganizations',
            type: 'readonly',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            defaultValue: this.translate('userManagement.all'),
          },
          {
            id: 'email',
            type: 'email',
            label: this.translate('userManagement.email'),
            required: true,
            placeholder: this.translate('userManagement.email'),
          },
          {
            id: 'mobile',
            type: 'phone',
            label: this.translate('userManagement.mobile'),
            required: false,
            placeholder: this.translate('userManagement.mobile'),
          },
        ];

      case UserType.SUB_ORGANIZATION_ADMIN:
        return [
          ...commonFields,
          {
            id: 'organization',
            type: 'readonly',
            label: this.translate('userManagement.organization'),
            required: false,
            defaultValue: this.userDetail?.organization?.id || '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.ORGANIZATION,
            ),
          },
          {
            id: 'subOrganizations',
            type: 'dropdown',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            options: getChildOrganizationUnitOptions(organizationUnits),
            placeholder: 'Select',
          },
          {
            id: 'email',
            type: 'email',
            label: this.translate('userManagement.email'),
            required: true,
            placeholder: this.translate('userManagement.email'),
          },
          {
            id: 'mobile',
            type: 'phone',
            label: this.translate('userManagement.mobile'),
            required: false,
            placeholder: this.translate('userManagement.mobile'),
          },
        ];

      case UserType.VENUE_ADMIN:
        return [
          ...commonFields,
          {
            id: 'subOrganizations',
            type: 'dropdown',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            options: getChildOrganizationUnitOptions(organizationUnits),
            placeholder: 'Select',
          },
          {
            id: 'venue',
            type: 'dropdown',
            label: this.translate('userManagement.venue'),
            required: true,
            options: [],
            placeholder: 'Select',
          },
          {
            id: 'email',
            type: 'email',
            label: this.translate('userManagement.email'),
            required: true,
            placeholder: this.translate('userManagement.email'),
          },
          {
            id: 'mobile',
            type: 'phone',
            label: this.translate('userManagement.mobile'),
            required: false,
            placeholder: this.translate('userManagement.mobile'),
          },
        ];

      case UserType.VENUE_MANAGER:
        return [
          ...commonFields,
          {
            id: 'subOrganizations',
            type: 'dropdown',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            options: getChildOrganizationUnitOptions(organizationUnits),
            placeholder: 'Select',
          },
          {
            id: 'venue',
            type: 'dropdown',
            label: this.translate('userManagement.venue'),
            required: true,
            options: [],
            placeholder: 'Select',
          },
          {
            id: 'venueBranch',
            type: 'multiselect',
            label: this.translate('userManagement.venueBranches'),
            required: true,
            options: [],
            placeholder: 'Select',
          },
          {
            id: 'email',
            type: 'email',
            label: this.translate('userManagement.email'),
            required: true,
            placeholder: this.translate('userManagement.email'),
          },
          {
            id: 'mobile',
            type: 'phone',
            label: this.translate('userManagement.mobile'),
            required: false,
            placeholder: this.translate('userManagement.mobile'),
          },
        ];

      case UserType.VENUE_STAFF:
        return [
          ...commonFields,
          {
            id: 'subOrganizations',
            type: 'dropdown',
            label: this.translate('userManagement.subOrganizations'),
            required: true,
            options: getChildOrganizationUnitOptions(organizationUnits),
            placeholder: 'Select',
          },
          {
            id: 'venue',
            type: 'dropdown',
            label: this.translate('userManagement.venue'),
            required: true,
            options: [],
            placeholder: 'Select',
          },
          {
            id: 'venueBranch',
            type: 'multiselect',
            label: this.translate('userManagement.venueBranches'),
            required: true,
            options: [],
            placeholder: 'Select',
          },
          {
            id: 'pin',
            type: 'pin',
            label: this.translate('userManagement.pin'),
            required: false,
            options: [
              {
                label: this.translate('userManagement.custom'),
                value: 'custom',
              },
              {
                label: this.translate('userManagement.autoGenerate'),
                value: 'default',
              },
            ],
          },
        ];
      default:
        const error: string = `Incorrect user type or user not allowed to access user type`;
        throw error;
    }
  }

  public getViewableFieldsForProfile(
    subject: GetUsersResponse,
  ): Record<string, string | number | undefined> {
    return {
      [this.translate('onboarding.firstName')]: subject.firstName,
      [this.translate('onboarding.lastName')]: subject.lastName,
      [this.translate('userManagement.userType')]: subject.type,
      [this.translate('userManagement.organization')]:
        subject.organization.name,
      [this.translate('userManagement.email')]: subject.email,
      [this.translate('userManagement.mobile')]: subject.mobileNumber,
    };
  }

  public getEditableFieldsForProfile(subject: GetUsersResponse): FieldConfig[] {
    return [
      {
        id: 'firstName',
        type: 'text',
        label: this.translate('userManagement.firstName'),
        required: true,
        placeholder: this.translate('userManagement.firstName'),
        defaultValue: subject.firstName,
      },
      {
        id: 'lastName',
        type: 'text',
        label: this.translate('userManagement.lastName'),
        required: true,
        placeholder: this.translate('userManagement.lastName'),
        defaultValue: subject.lastName,
      },
      {
        id: 'userType',
        type: 'readonly',
        label: this.translate('userManagement.userTypeTitle'),
        placeholder: this.translate('userManagement.userTypeTitle'),
        required: true,
        defaultValue: subject.type,
      },
      {
        id: 'organization',
        type: 'readonly',
        label: this.translate('userManagement.organization'),
        required: true,
        defaultValue: subject.organization.name,
      },
      {
        id: 'mobile',
        type: 'phone',
        label: this.translate('userManagement.mobile'),
        required: false,
        placeholder: this.translate('userManagement.mobile'),
        defaultValue: subject?.mobileNumber,
      },
      {
        id: 'email',
        type: 'readonly',
        label: this.translate('userManagement.email'),
        required: true,
        placeholder: this.translate('userManagement.email'),
        defaultValue: subject?.email,
      },
    ];
  }
}
